import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser, palmettoFinanceOrgAlias } from '@/components/auth/RequirePalmettoFinanceUser';
import { debounceAsync } from '@/helpers/debounceAsync';
import { useCalculateItcMutation } from '@/services/itc';
import { useLazySearchOrganizationsQuery } from '@/services/organizations';
import { Box, toast } from '@palmetto/palmetto-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { AvlFilterOption, DomesticContentAttestmentType, MountingType, ProgramType } from 'types';

import { AsyncSelectInput } from '../Forms/AsyncSelect';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { PVEquipment } from '../SystemDesign/PVEquipment';

import { ITCCalculateButton } from './ITCCalculator/ITCCalculateButton';
import { ITCCalculatorResults } from './ITCCalculator/ITCCalculatorResults';

const defaultMountingHardware = [
  { type: 'rail' },
  { type: 'fastenerClamp' },
  { type: 'fastenerMlpe' },
  { type: 'fastenerSplice' },
];

export const ITCCalculator = () => {
  const { claims } = useAuth();
  const [calculateItc, { isLoading, data, reset }] = useCalculateItcMutation();
  const isSabalUser = isPalmettoFinanceUser(claims);
  const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | undefined>(undefined);
  const orgAlias = isSabalUser ? (selectedOption?.value ?? palmettoFinanceOrgAlias) : claims?.org;

  const [triggerSearchOrgs] = useLazySearchOrganizationsQuery();

  const handleOrgSearch = useMemo(
    () =>
      debounceAsync(async (searchText: string) => {
        const result = await triggerSearchOrgs({ searchTerm: searchText, useRootOrganization: true });
        return (
          result?.data?.flat?.map((org) => ({
            value: org.alias,
            label: `${org.name} (${org.alias})`,
          })) ?? []
        );
      }, 400),
    [triggerSearchOrgs],
  );

  const handleOrgChange = useCallback((event: any) => setSelectedOption(event.target.value ?? undefined), []);

  const onSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    try {
      const systemDesign = {
        panelManufacturer: values.panelManufacturer,
        panelModel: values.panelModel,
        inverters:
          values.inverterModels
            ?.map((inverter: { model: string; count: number; vintage?: string }) => {
              if (!values.inverterManufacturer || !inverter.model) return undefined;
              const result: any = {
                manufacturer: values.inverterManufacturer,
                model: inverter.model,
                count: inverter.count || 1,
              };
              if (inverter.vintage && inverter.vintage !== 'none') {
                result.vintage = inverter.vintage;
              }
              return result;
            })
            .filter(Boolean) || [],
        mountingType: values.mountingType,
        mountingHardware: values.mountingHardware,
        storage:
          values.storageModels
            ?.map((storage: { model: string; count: number; vintage?: string }) => {
              if (!values.storageManufacturer || !storage.model) return undefined;
              const result: any = {
                manufacturer: values.storageManufacturer,
                model: storage.model,
                count: storage.count || 1,
              };
              if (storage.vintage && storage.vintage !== 'none') {
                result.vintage = storage.vintage;
              }
              return result;
            })
            .filter(Boolean) || [],
        enphaseInverterAttested: values.inverterManufacturer === 'Enphase' ? !!values.enphaseInverterAck : undefined,
        enphaseStorageAttested: values.storageManufacturer === 'Enphase' ? !!values.enphaseStorageAck : undefined,
      };

      await calculateItc(systemDesign).unwrap();

      // In order to set the new 'baseline' state to detect if the form is dirty,
      // we reset the form with the current values on a successful calculation.
      formikHelpers.resetForm({ values });
    } catch (err: any) {
      console.error('ITC calculation call failed:', err);
      toast.error(err?.data?.message || 'Failed to calculate ITC eligibility');
    }
  };

  return (
    <>
      <Helmet>
        <title>ITC Eligibility Calculator</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          title="ITC Eligibility Calculator"
          description="Check the eligibility of a project for FEOC compliance and Domestic Content ITC adder eligibility"
        />
        <Box
          padding={{
            base: '0 lg',
            tablet: '0',
          }}
        >
          {isSabalUser && (
            <AsyncSelectInput
              id="itc-calculator-org-picker"
              label="View calculator in context of org"
              loadOptions={handleOrgSearch}
              onChange={handleOrgChange}
              value={selectedOption ?? null}
              isClearable
              placeholder="Search for an org..."
            />
          )}
          <Formik
            initialValues={{
              mountingType: MountingType.roof,
              inverterModels: [{ model: '', count: 1 }],
              storageModels: [{ model: '', count: 1 }],
              mountingHardware: defaultMountingHardware,
            }}
            onSubmit={onSubmit}
          >
            <Form>
              <Box direction="column" margin="xl 0" childGap="xl" justifyContent="center">
                <ITCCalculatorResults data={data} reset={reset} />
                <PVEquipment
                  programType={ProgramType.solar}
                  hideQuantity
                  showStorageOptions
                  avlFilterOption={AvlFilterOption.itcCalculator}
                  orgAlias={orgAlias}
                  isHardwareOverridden
                  attestmentType={DomesticContentAttestmentType.pvAndStorage}
                  showOptimizerCheckbox
                  showEnphaseCheckboxes
                  allowDeleteMountingHardware={false}
                  readOnly={!!data}
                  hideMountingType
                  removeModelWhenManufacturerChanges
                  allowMultipleBatteries
                  useSearchableModelSelect
                />
              </Box>
              <Box
                direction={{
                  base: 'column',
                  tablet: 'row',
                }}
                alignItems={{
                  base: 'stretch',
                  tablet: 'flex-end',
                }}
                justifyContent={{
                  tablet: 'flex-end',
                }}
                childGap="sm"
                style={{ flexShrink: 0 }}
                padding="lg 0 0 0"
              >
                <ITCCalculateButton isLoading={isLoading} size="md" variant="primary" disabled={!!data}>
                  Calculate
                </ITCCalculateButton>
              </Box>
            </Form>
          </Formik>
        </Box>
      </MainContainer>
    </>
  );
};
